import * as K from 'common/constants';
import {
  FILTER_STATE,
  TOKEN_TYPE,
} from './types';

// token CSS class (if not the same as the token type)
export const TOKEN_CLASS: Record<string, string> = {};
TOKEN_CLASS[TOKEN_TYPE.AND]           = 'token-operator token-boolean';
TOKEN_CLASS[TOKEN_TYPE.COMPARISON]    = 'token-operator token-comparison';
TOKEN_CLASS[TOKEN_TYPE.MATH_OPERATOR] = 'token-operator token-math';
TOKEN_CLASS[TOKEN_TYPE.NOT]           = 'token-operator token-negation';
TOKEN_CLASS[TOKEN_TYPE.OR]            = 'token-operator token-boolean';
TOKEN_CLASS[TOKEN_TYPE.TIME_ZONE]     = 'token-timezone';

// Constants related to parsing a filter string

// Scalyr functions
export const GRAPH_FUNCTIONS = [ 'count', 'countPerSecond', 'fraction', 'max', 'mean', 'median', 'min', 'p', 'p10', 'p50', 'p90', 'p95', 'p99', 'p999', 'sum', 'sumPerSecond', 'sumPerSec' ];
export const TIME_FUNCTIONS  = [ 'dayOfMonth', 'dayOfWeek', 'hourOfDay', 'minuteOfHour' ]; // can take timezone as arg
export const FUNCTIONS       = [ ...GRAPH_FUNCTIONS, ...TIME_FUNCTIONS ];

// Power Query commands
export const COMMANDS = [ 'columns', 'filter', 'group', 'inner join', 'join', 'left join', 'let', 'limit', 'lookup', 'parse', 'sort', 'transpose', 'union' ];

// Power Query commands that can have subqueries and that do not allow an initial filter
export const COMPLEX_COMMANDS = [ 'inner join', 'join', 'left join', 'union' ];

// Power Query functions
export const PQ_FUNCTIONS: Record<TOKEN_TYPE, string[]> = {} as Record<TOKEN_TYPE, string[]>;
Object.values(TOKEN_TYPE).forEach((key) => { PQ_FUNCTIONS[key as TOKEN_TYPE] = []; });

PQ_FUNCTIONS[TOKEN_TYPE.PQ_GROUP_FUNCTION] = [
  'any', 'average', 'avg', 'count', 'estimate_distinct', 'first', 'last', 'max', 'mean', 'median', 'min',
  'newest', 'oldest', 'p10', 'p50', 'p90', 'p95', 'p99', 'p999', 'pct', 'stddev', 'sum',
];

PQ_FUNCTIONS[TOKEN_TYPE.PQ_AGGREGATION_FUNCTION] = [
  'overall_average', 'overall_avg', 'overall_count', 'overall_max', 'overall_mean', 'overall_min', 'overall_sum',
  'percent_of_total', 'running_count', 'running_percent', 'running_sum',
];

PQ_FUNCTIONS[TOKEN_TYPE.PQ_NUMERIC_FUNCTION] = [
  'abs', 'ceiling', 'exp', 'floor', 'ln', 'log', 'max', 'min', 'pow', 'sqrt',
];

PQ_FUNCTIONS[TOKEN_TYPE.PQ_STRING_FUNCTION] = [
  'format', 'isblank', 'isempty', 'len', 'lower', 'ltrim', 'replace', 'rtrim', 'string', 'substr', 'trim', 'upper',
];

PQ_FUNCTIONS[TOKEN_TYPE.PQ_TIME_FUNCTION] = [
  'queryend', 'queryspan', 'querystart', 'timebucket',
];

PQ_FUNCTIONS[TOKEN_TYPE.PQ_NETWORK_FUNCTION] = [
  'net_ip', 'net_ipv4', 'net_ipv6', 'net_ipsubnet', 'net_private', 'net_rfc1918', 'net_rfc4193',
];

// functions with no args can be completed with ()
export const PQ_FUNCTIONS_NO_ARGS = [ 'count', 'running_count', 'overall_count' ];

// Power Query function states
export const PQ_IN_FUNCTION_STATE: Record<TOKEN_TYPE, FILTER_STATE | null> = {} as Record<TOKEN_TYPE, FILTER_STATE | null>;
Object.values(TOKEN_TYPE).forEach((key) => { PQ_IN_FUNCTION_STATE[key as TOKEN_TYPE] = null; });
PQ_IN_FUNCTION_STATE[TOKEN_TYPE.PQ_GROUP_FUNCTION]       = FILTER_STATE.IN_PQ_GROUP_FUNCTION;
PQ_IN_FUNCTION_STATE[TOKEN_TYPE.PQ_AGGREGATION_FUNCTION] = FILTER_STATE.IN_PQ_AGGREGATION_FUNCTION;
PQ_IN_FUNCTION_STATE[TOKEN_TYPE.PQ_NETWORK_FUNCTION]     = FILTER_STATE.IN_PQ_NETWORK_FUNCTION;
PQ_IN_FUNCTION_STATE[TOKEN_TYPE.PQ_NUMERIC_FUNCTION]     = FILTER_STATE.IN_PQ_NUMERIC_FUNCTION;
PQ_IN_FUNCTION_STATE[TOKEN_TYPE.PQ_STRING_FUNCTION]      = FILTER_STATE.IN_PQ_STRING_FUNCTION;
PQ_IN_FUNCTION_STATE[TOKEN_TYPE.PQ_TIME_FUNCTION]        = FILTER_STATE.IN_PQ_TIME_FUNCTION;

// Regexes are run in order; first match wins.
export const REGEXES = [
  { type: TOKEN_TYPE.FUNCTION,     regex: new RegExp('^(' + FUNCTIONS.join('|') + ')(\\(|:)') }, // mean p99 dayOfWeek
  { type: TOKEN_TYPE.FUNCTION,     regex: /^(p\[[\d.]+\])/ },                                    // p[84]
  { type: TOKEN_TYPE.WORD,         regex: /^((\$([\w-.]|\\.)|[a-zA-Z:_])([\w\-:.]|\\.)*)/ },     // bytes $bytes $\. 12abc where foo-bar foo.bar :foo a:b _type
  { type: TOKEN_TYPE.NUMBER,       regex: /^(-?\d[\d_]*\.\d+)\b/ },                              // 3.25 -412.8 1_253.6
  { type: TOKEN_TYPE.NUMBER,       regex: /^(-?\.\d+)\b/ },                                      // .73 -.593
  { type: TOKEN_TYPE.NUMBER,       regex: /^(-?\d[\d_]*)\b/ },                                   // 5000 5_007 -83
];

// Two-character tokens
export const TWO_CHARS = [
  { type: TOKEN_TYPE.AND,        list: [ '&&' ] },
  { type: TOKEN_TYPE.COMPARISON, list: [ '==', '!=', '>=', '<=' ] },
  { type: TOKEN_TYPE.OR,         list: [ '||' ] },
];

// One-character tokens
export const ONE_CHAR = [
  { type: TOKEN_TYPE.COLON,         list: [ ':' ] },
  { type: TOKEN_TYPE.COMMA,         list: [ ',' ] },
  { type: TOKEN_TYPE.COMPARISON,    list: [ '=', '>', '<' ] },
  { type: TOKEN_TYPE.MATH_OPERATOR, list: [ ...Object.values<string>(K.MATH_OPERATOR) ] },
  { type: TOKEN_TYPE.NOT,           list: [ '!' ] },
  { type: TOKEN_TYPE.QUESTION_MARK, list: [ '?' ] },
];

// Identifiable words
export const WORDS: Record<TOKEN_TYPE, string[]> = {} as Record<TOKEN_TYPE, string[]>;
Object.values(TOKEN_TYPE).forEach((key) => { WORDS[key as TOKEN_TYPE] = []; });
WORDS[TOKEN_TYPE.AND]        = [ 'and', 'AND' ];
WORDS[TOKEN_TYPE.BOOLEAN]    = [ 'true', 'false' ];
WORDS[TOKEN_TYPE.COMPARISON] = [ 'contains', 'matches', 'in' ];
WORDS[TOKEN_TYPE.MODIFIER]   = [ 'matchcase' ];
WORDS[TOKEN_TYPE.NOT]        = [ 'not', 'NOT' ];
WORDS[TOKEN_TYPE.OR]         = [ 'or', 'OR' ];

// ordered list of comparison operators
export const COMPARATORS = [ '=', '!=', '<', '<=', '>', '>=', 'contains', 'matches', 'in' ];

// operators that positively restrict matches
export const CONSTRAINING_OPERATORS = [ '=', '==', 'contains' ];

// used to negate a term
export const INVERSE: Record<string, string> = {
  '=':  '!=',
  '==': '!=',
  '!=': '=',
  '<':  '>=',
  '>':  '<=',
  '<=': '>',
  '>=': '<',
};

// aggregate token types are aliases for commonly seen groups of token types
export const AGGREGATE_TOKEN_TYPE: Record<TOKEN_TYPE, Array<TOKEN_TYPE>> = {} as Record<TOKEN_TYPE, Array<TOKEN_TYPE>>;
Object.values(TOKEN_TYPE).forEach((key) => { AGGREGATE_TOKEN_TYPE[key as TOKEN_TYPE] = []; });
AGGREGATE_TOKEN_TYPE[TOKEN_TYPE.PQ_COLUMN] = [ TOKEN_TYPE.FIELD, TOKEN_TYPE.PQ_FIELD ];
AGGREGATE_TOKEN_TYPE[TOKEN_TYPE.PQ_EXPRESSION_FUNCTION] = [
  TOKEN_TYPE.PQ_AGGREGATION_FUNCTION,
  TOKEN_TYPE.PQ_NETWORK_FUNCTION,
  TOKEN_TYPE.PQ_NUMERIC_FUNCTION,
  TOKEN_TYPE.PQ_STRING_FUNCTION,
  TOKEN_TYPE.PQ_TIME_FUNCTION,
];
AGGREGATE_TOKEN_TYPE[TOKEN_TYPE.PQ_EXPRESSION] = [
  TOKEN_TYPE.FIELD,
  TOKEN_TYPE.NUMBER,
  TOKEN_TYPE.BOOLEAN,
  TOKEN_TYPE.STRING,
  TOKEN_TYPE.NOT,
  TOKEN_TYPE.PQ_FIELD,
  TOKEN_TYPE.PQ_AGGREGATION_FUNCTION,
  TOKEN_TYPE.PQ_NETWORK_FUNCTION,
  TOKEN_TYPE.PQ_NUMERIC_FUNCTION,
  TOKEN_TYPE.PQ_STRING_FUNCTION,
  TOKEN_TYPE.PQ_TIME_FUNCTION,
];

export const EMPTY_FILTER_STATE = {
  states: [] as FILTER_STATE[],
  valid:  [] as TOKEN_TYPE[],
  text:   '',
  field:  '',
  op:     '',
};

// this map tells us what types of things to complete against based on a state
export const FILTER_STATE_NEXT: Record<FILTER_STATE, Array<TOKEN_TYPE>> = {} as Record<FILTER_STATE, Array<TOKEN_TYPE>>;
Object.values(FILTER_STATE).forEach((key) => { FILTER_STATE_NEXT[key as FILTER_STATE] = []; });

// map filter state to the types of tokens that can come next, and that can be autocompleted
FILTER_STATE_NEXT[FILTER_STATE.DOLLAR]              = [ TOKEN_TYPE.FIELD, TOKEN_TYPE.REGEX ];
FILTER_STATE_NEXT[FILTER_STATE.FIELD]               = [ TOKEN_TYPE.FIELD, TOKEN_TYPE.COMPARISON ];
FILTER_STATE_NEXT[FILTER_STATE.FIELD_IN_FUNCTION]   = [ TOKEN_TYPE.WHERE, TOKEN_TYPE.TIMESHIFT ];
FILTER_STATE_NEXT[FILTER_STATE.LIST]                = [ TOKEN_TYPE.VALUE ];
FILTER_STATE_NEXT[FILTER_STATE.MODIFIED_COMPARATOR] = [ TOKEN_TYPE.MODIFIER ];
FILTER_STATE_NEXT[FILTER_STATE.PIPE]                = [ TOKEN_TYPE.COMMAND ];
FILTER_STATE_NEXT[FILTER_STATE.START]               = [ TOKEN_TYPE.FIELD, TOKEN_TYPE.FUNCTION, TOKEN_TYPE.STRING ];
FILTER_STATE_NEXT[FILTER_STATE.TERM]                = [ TOKEN_TYPE.FIELD, TOKEN_TYPE.STRING, TOKEN_TYPE.BOOLEAN ];
FILTER_STATE_NEXT[FILTER_STATE.TIMESHIFT_INTERVAL]  = [ TOKEN_TYPE.WHERE ];
FILTER_STATE_NEXT[FILTER_STATE.WORD]                = [ TOKEN_TYPE.FIELD, TOKEN_TYPE.COMPARISON ];

// filter states for comparators
FILTER_STATE_NEXT[FILTER_STATE.COMPARATOR_EQUALITY] = [ TOKEN_TYPE.VALUE, TOKEN_TYPE.BOOLEAN ];
FILTER_STATE_NEXT[FILTER_STATE.COMPARATOR_NUMERIC]  = [ TOKEN_TYPE.VALUE ];
FILTER_STATE_NEXT[FILTER_STATE.COMPARATOR_CONTAINS] = [ TOKEN_TYPE.STRING ];
FILTER_STATE_NEXT[FILTER_STATE.COMPARATOR_MATCHES]  = [ TOKEN_TYPE.REGEX ];
// FILTER_STATE_NEXT[FILTER_STATE.COMPARATOR_IN]       = [ EXPRESSION_TYPE.LIST ];

// filter states where the last token is not complete
FILTER_STATE_NEXT[FILTER_STATE.IN_COMMAND]       = [ TOKEN_TYPE.COMMAND ];
FILTER_STATE_NEXT[FILTER_STATE.IN_COMPARATOR]    = [ TOKEN_TYPE.COMPARISON ];
FILTER_STATE_NEXT[FILTER_STATE.IN_NUMBER_VALUE]  = [ TOKEN_TYPE.VALUE ];
FILTER_STATE_NEXT[FILTER_STATE.IN_STRING_VALUE]  = [ TOKEN_TYPE.VALUE ];
FILTER_STATE_NEXT[FILTER_STATE.IN_WORD]          = [ TOKEN_TYPE.FIELD ];
FILTER_STATE_NEXT[FILTER_STATE.IN_WORD_START]    = [ TOKEN_TYPE.FIELD, TOKEN_TYPE.FUNCTION ];
FILTER_STATE_NEXT[FILTER_STATE.IN_WORD_VALUE]    = [ TOKEN_TYPE.BOOLEAN, TOKEN_TYPE.VALUE ];

// Power Query states where last token is complete
FILTER_STATE_NEXT[FILTER_STATE.PQ_EXPRESSION_START] = [ TOKEN_TYPE.PQ_EXPRESSION ];
FILTER_STATE_NEXT[FILTER_STATE.PQ_GROUP]            = [ TOKEN_TYPE.PQ_GROUP_FUNCTION ];
FILTER_STATE_NEXT[FILTER_STATE.PQ_JOIN_ON]          = [ TOKEN_TYPE.PQ_COLUMN ];
FILTER_STATE_NEXT[FILTER_STATE.PQ_SUBQUERIES]       = [ TOKEN_TYPE.ON ];
FILTER_STATE_NEXT[FILTER_STATE.PQ_TABLE]            = [ TOKEN_TYPE.BY ];
FILTER_STATE_NEXT[FILTER_STATE.PQ_TABLE_COLUMN]     = [ TOKEN_TYPE.FROM ];

// Power Query states where last token is not complete
FILTER_STATE_NEXT[FILTER_STATE.IN_PQ_COLUMN]                 = [ TOKEN_TYPE.PQ_COLUMN ];
FILTER_STATE_NEXT[FILTER_STATE.IN_PQ_EXPRESSION_START]       = [ TOKEN_TYPE.PQ_EXPRESSION ];
FILTER_STATE_NEXT[FILTER_STATE.IN_PQ_AGGREGATION_FUNCTION]   = [ TOKEN_TYPE.PQ_EXPRESSION ];
FILTER_STATE_NEXT[FILTER_STATE.IN_PQ_GROUP_FUNCTION]         = [ TOKEN_TYPE.PQ_EXPRESSION ];
FILTER_STATE_NEXT[FILTER_STATE.IN_PQ_NETWORK_FUNCTION]       = [ TOKEN_TYPE.PQ_COLUMN, TOKEN_TYPE.STRING ];
FILTER_STATE_NEXT[FILTER_STATE.IN_PQ_NUMERIC_FUNCTION]       = [ TOKEN_TYPE.PQ_COLUMN, TOKEN_TYPE.NUMBER, TOKEN_TYPE.PQ_NUMERIC_FUNCTION ];
FILTER_STATE_NEXT[FILTER_STATE.IN_PQ_STRING_FUNCTION]        = [ TOKEN_TYPE.PQ_COLUMN, TOKEN_TYPE.STRING, TOKEN_TYPE.PQ_NUMERIC_FUNCTION ];
FILTER_STATE_NEXT[FILTER_STATE.IN_PQ_TABLE_COLUMN]           = [ TOKEN_TYPE.FROM ];
FILTER_STATE_NEXT[FILTER_STATE.IN_PQ_TIME_FUNCTION]          = [ TOKEN_TYPE.NUMBER, TOKEN_TYPE.STRING ];

// filter state based on a single character
export const CHAR_STATE: Record<string, FILTER_STATE> = {
  $:   FILTER_STATE.DOLLAR,
  '(': FILTER_STATE.START,
  ')': FILTER_STATE.START,
  '|': FILTER_STATE.PIPE,
};

// whether we should perform completion on an empty string
export const COMPLETE_EMPTY_STRING: Record<TOKEN_TYPE, boolean> = {} as Record<TOKEN_TYPE, boolean>;
Object.keys(COMPLETE_EMPTY_STRING).forEach((key) => { COMPLETE_EMPTY_STRING[key as TOKEN_TYPE] = false; });
COMPLETE_EMPTY_STRING[TOKEN_TYPE.COMMAND]    = true;
COMPLETE_EMPTY_STRING[TOKEN_TYPE.COMPARISON] = true;
COMPLETE_EMPTY_STRING[TOKEN_TYPE.MODIFIER]   = true;
COMPLETE_EMPTY_STRING[TOKEN_TYPE.TIMESHIFT]  = true;
COMPLETE_EMPTY_STRING[TOKEN_TYPE.VALUE]      = true; // show user possible facet values
COMPLETE_EMPTY_STRING[TOKEN_TYPE.WHERE]      = true;

// Power queries
COMPLETE_EMPTY_STRING[TOKEN_TYPE.BY]   = true;
COMPLETE_EMPTY_STRING[TOKEN_TYPE.FROM] = true;
COMPLETE_EMPTY_STRING[TOKEN_TYPE.ON]   = true;
